<script setup>
/** 一条视频记录的完整链路：对局 → 请求 → 改编分镜 → 逐场生成 → 拼接成片。 */
import { computed, onMounted, ref } from 'vue'
import { ArrowLeft } from '@lucide/vue'
import { Button, PageHeader, Section, Spinner } from '@drama/ui'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { getGenerationRecordProcess } from '../api/generation-records.js'
import AssemblyStrip from './generation-record-process/AssemblyStrip.vue'
import ProcessOverview from './generation-record-process/ProcessOverview.vue'
import SceneCard from './generation-record-process/SceneCard.vue'
import TranscriptTimeline from './generation-record-process/TranscriptTimeline.vue'

const props = defineProps({
  id: { type: String, required: true },
})

const { t } = useI18n()
const route = useRoute()
const router = useRouter()

const loading = ref(false)
const error = ref('')
const process = ref(null)

onMounted(load)

async function load() {
  loading.value = true
  error.value = ''
  try {
    process.value = await getGenerationRecordProcess(props.id)
  } catch (requestError) {
    error.value = requestError.message
  } finally {
    loading.value = false
  }
}

function back() {
  if (route.name === 'generation-record-process' && window.history.length > 1) {
    router.back()
    return
  }
  router.push({ name: 'generation-records' })
}

const sceneNoByTurn = computed(() => new Map((process.value?.screenplay?.scenes ?? []).map(scene => [scene.turn_no, scene.scene_no])))

/** 按回合号取对局记录；开场几条都在第 0 回合。 */
const transcriptByTurn = computed(() => {
  const byTurn = new Map()
  for (const entry of process.value?.run?.transcript ?? []) {
    byTurn.set(entry.turn, [...(byTurn.get(entry.turn) ?? []), entry])
  }
  return byTurn
})

/** 分镜和它的生产结果并排，按场次顺序。 */
const scenes = computed(() => {
  const screenplay = process.value?.screenplay ?? null
  if (screenplay === null) return []
  const segmentBySceneNo = new Map((process.value.production?.segments ?? []).map(segment => [segment.scene_no, segment]))
  return [...screenplay.scenes]
    .sort((a, b) => a.scene_no - b.scene_no)
    .map(scene => ({ scene, segment: segmentBySceneNo.get(scene.scene_no) ?? null }))
})

function chainFrom(index) {
  if (index === 0) return null
  const previous = scenes.value[index - 1]
  return { sceneNo: previous.scene.scene_no, endFrameUrl: previous.segment?.end_frame_url ?? null }
}
</script>

<template>
  <div class="generation-record-process-page vga-page">
    <PageHeader :title="t('visualGameAdmin.generationRecords.process.title')">
      <template #actions>
        <Button variant="secondary" size="sm" @click="back">
          <ArrowLeft :size="14" />
          {{ t('visualGameAdmin.generationRecords.process.back') }}
        </Button>
      </template>
    </PageHeader>

    <p class="generation-record-process-page__id">
      {{ t('visualGameAdmin.generationRecords.process.videoId') }}: <code>{{ id }}</code>
      <template v-if="process?.video_request.run_id">
        · {{ t('visualGameAdmin.generationRecords.process.runId') }}: <code>{{ process.video_request.run_id }}</code>
      </template>
    </p>

    <div v-if="loading" class="vga-state">
      <Spinner />
      <span>{{ t('visualGameAdmin.common.loading') }}</span>
    </div>
    <div v-else-if="error" class="vga-state">
      <p class="vga-error">{{ error }}</p>
      <Button variant="secondary" @click="load">{{ t('visualGameAdmin.common.retry') }}</Button>
    </div>

    <template v-else-if="process">
      <Section>
        <ProcessOverview :process="process" />
      </Section>

      <Section :title="t('visualGameAdmin.generationRecords.process.transcript.title')">
        <TranscriptTimeline v-if="process.run && process.run.transcript.length > 0" :run="process.run" :scene-no-by-turn="sceneNoByTurn" />
        <p v-else class="generation-record-process-page__empty">{{ t('visualGameAdmin.generationRecords.process.empty') }}</p>
      </Section>

      <Section :title="t('visualGameAdmin.generationRecords.process.scene.title')">
        <template v-if="scenes.length > 0">
          <SceneCard
            v-for="({ scene, segment }, index) in scenes"
            :key="scene.scene_no"
            :scene="scene"
            :segment="segment"
            :source-entries="transcriptByTurn.get(scene.turn_no) ?? []"
            :chain-from="chainFrom(index)"
          />
        </template>
        <p v-else class="generation-record-process-page__empty">{{ t('visualGameAdmin.generationRecords.process.empty') }}</p>
      </Section>

      <Section :title="t('visualGameAdmin.generationRecords.process.assembly.title')">
        <AssemblyStrip
          v-if="process.production"
          :production="process.production"
          :scenes="scenes"
          :final-video="process.final_video"
          :segment-count="process.video_request.segment_count"
        />
        <p v-else class="generation-record-process-page__empty">{{ t('visualGameAdmin.generationRecords.process.empty') }}</p>
      </Section>
    </template>
  </div>
</template>

<style scoped>
.generation-record-process-page {
  display: flex;
  flex-direction: column;
  gap: var(--drama-space-lg);
}

.generation-record-process-page__id {
  margin: 0;
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-sm);
}

.generation-record-process-page__id code {
  font-family: var(--drama-font-family-mono);
}

.generation-record-process-page__empty {
  margin: 0;
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-sm);
}
</style>
